import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions, DialogContent } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog, DialogTitle } from '@components/core/dialog';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import SelectWithCategoriesLov from '@components/core/select-with-categories/select-with-categories-lov';
import TextField from '@components/core/text-field';
import Constants from '@utils/constants';
import { validateFields } from '@utils/validation';
import { saveNamedItem_OpraMutations_saveNamedItem_NamedItem } from '@queries/generated/common.generated';
import {
    LibraryControlFields_LibraryControl_responsibleFunction_NamedItem,
    LibraryFunctionalDataReviewFields,
} from '@queries/generated/fragments.generated';
import {
    GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
    UPSERT_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
} from '@queries/legacy-risk-assessment';
import './styles.scss';

interface IAddFunctionalDataReviewDialog {
    onClose: () => void;
    onSubmit: (result?: string | undefined) => void;
    functionalDataReview: LibraryFunctionalDataReviewFields;
    responsibleFunctions: saveNamedItem_OpraMutations_saveNamedItem_NamedItem[];
}

const AddFunctionalDataReviewDialog = ({
    onClose,
    onSubmit,
    functionalDataReview,
    responsibleFunctions,
}: IAddFunctionalDataReviewDialog) => {
    const { t } = useTranslation();

    const [saveLibraryRisk] = useMutation(UPSERT_LIBRARY_FUNCTIONAL_DATA_REVIEWS);
    type State = {
        id: number | null;
        active: boolean;
        responsibleFunction:
            | Pick<saveNamedItem_OpraMutations_saveNamedItem_NamedItem, 'name' | 'id' | 'active'>
            | null
            | undefined;
        description: string;
    };

    const initialState: State = {
        id: null,
        active: true,
        responsibleFunction: null,
        description: '',
    };
    const [filteredResponsibleFunctions, setFilteredResponsibleFunctions] = useState<
        saveNamedItem_OpraMutations_saveNamedItem_NamedItem[]
    >([]);
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);
    const disabledAdd = useBoolean(true);

    useEffect(() => {
        setFilteredResponsibleFunctions(
            responsibleFunctions.filter((item) => item.studyId === Constants.ObjectId.Empty && item.active),
        );
    }, [responsibleFunctions]);

    useEffect(() => {
        if (functionalDataReview) {
            setState({
                ...state,
                id: functionalDataReview.id,
                active: functionalDataReview.active ?? false,
                description: functionalDataReview.description ?? '',
                responsibleFunction: functionalDataReview.responsibleFunction ?? null,
            });
        } else {
            setState(initialState);
        }
    }, [functionalDataReview]);

    const close = () => {
        onClose();
    };

    const onChangeDescription = () => (event: any) => {
        setState({
            ...state,
            description: event.target.value,
        });

        const valid = validateFields(event.target.value, 'description', 600, true);
        if (valid) {
            setErrors({
                ...errors,
                description: valid.description,
            });
            if (valid.description) {
                disabledAdd.setTrue();
            } else {
                disabledAdd.setFalse();
            }
        }
    };

    const onChangeResponsibleFunction = () => (event: any) => {
        const { value } = event.target;
        const responsibleFunction = filteredResponsibleFunctions.find(
            (item: LibraryControlFields_LibraryControl_responsibleFunction_NamedItem) => item.id === value,
        );

        setState({
            ...state,
            responsibleFunction,
        });
    };

    useEffect(() => {
        if (!functionalDataReview) {
            return;
        }

        if (state?.active != functionalDataReview?.active) {
            disabledAdd.setFalse();
            return;
        }

        if (state?.responsibleFunction?.id != functionalDataReview?.responsibleFunction?.id) {
            disabledAdd.setFalse();
            return;
        }

        if (state?.description != null && state?.description != functionalDataReview.description) {
            disabledAdd.setFalse();
            return;
        }

        disabledAdd.setTrue();
    }, [state.active, state.description, state.responsibleFunction]);

    const submit = () => {
        const isNew = !state.id;

        saveLibraryRisk({
            variables: {
                libraryFunctionalDataReview: {
                    id: state.id,
                    active: state.active,
                    responsibleFunctionId: state?.responsibleFunction?.id || null,
                    description: state.description,
                },
            },
            refetchQueries: [
                {
                    query: GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
                },
            ],
        }).then(() => {
            onSubmit(t(isNew ? 'ra:functionalDataReviewAdded' : 'ra:functionalDataReviewUpdated'));
        });
    };

    return (
        <CustomDialog
            className="custom-width-popup max-w700"
            onClose={() => {}}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
        >
            <DialogTitle id="add-library-fdr-modal-title">
                {functionalDataReview ? t('ra:editFunctionalDataReview') : t('ra:addFunctionalDataReview')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}>
                <div className="k-mr">
                    <Checkbox
                        rounded="none"
                        id="FunctionalDataReview-is-active"
                        className="cell-checkbox flex-start no-border no-full-width"
                        checked={state.active}
                        onChange={() => {
                            setState({
                                ...state,
                                active: !state.active,
                            });
                        }}
                        label={t('active')}
                        data-testid="FunctionalDataReview-is-active"
                        size="medium"
                    />
                </div>
                <div className="k-mr k-mt-8">
                    <TextField
                        id="description"
                        autoFocus
                        label={t('ra:description')}
                        fullWidth
                        multiline
                        rows={4}
                        value={state.description}
                        onChange={onChangeDescription()}
                        size="small"
                        placeholder={t('ra:enterDescription')}
                        helperText={
                            <CharactersRemaining
                                id="functionalDataReview-description-characters"
                                length={state.description.length}
                                max={600}
                                error={errors.description}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        required
                        error={!!errors.comments}
                    />
                </div>
                <div className="k-mr k-mt-8">
                    <SelectWithCategoriesLov
                        label={t('ra:responsibleFunction')}
                        onChange={onChangeResponsibleFunction}
                        namedItems={filteredResponsibleFunctions}
                        labelField="responsibleFunction"
                        fieldSelect="responsibleFunction"
                        customId="add-functional-data-review"
                        error={errors.responsibleFunction}
                        currentItem={state.responsibleFunction}
                        defaultText={t('ra:selectResponsibleFunction')}
                    />
                </div>
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!functionalDataReview ? 'flex-end' : ''}`}
            >
                <ModalUpdatedInfo item={functionalDataReview} />
                <Button
                    text={functionalDataReview ? t('common:update') : t('common:add')}
                    id="Update-fdr-close"
                    onClick={submit}
                    disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledAdd.value}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddFunctionalDataReviewDialog;
